import { Howl } from 'howler';
import { SOUNDS, type SoundKey } from './registry';
import { useUiStore } from '../store/uiStore';

/**
 * Тонка обгортка над Howler з тихим фолбеком.
 *
 * Головна вимога: відсутній звуковий файл НЕ повинен ані ламати гру, ані
 * сипати помилками в консоль дитині. Тому кожен Howl створюється лінько, а
 * перша помилка завантаження назавжди позначає ключ як «немає файлу».
 *
 * Другa вимога зʼявилася після того, як виявилось, що звуку в застосунку не
 * було НІКОДИ: файлів у public/sounds не існувало, сервер віддавав на них
 * index.html із кодом 200, Web Audio не міг це декодувати — і тихий фолбек
 * чесно ковтав кожну спробу. Місяцями. Тому стан завантаження тепер видно
 * назовні: кабінет батьків показує, що звук увімкнено, а файли не приїхали.
 */

/** Стан одного ключа. */
type SoundLoad = 'loading' | 'ready' | 'broken';

/**
 * Стан набору в цілому:
 *  · unknown — ще нічого не пробували або спроба триває, казати нічого;
 *  · ready   — хоч один файл справді завантажився, звук працює;
 *  · broken  — усі спроби провалились, на цьому пристрої буде тихо.
 */
export type SoundStatus = 'unknown' | 'ready' | 'broken';

/**
 * Агрегат станів. Виділений в чисту функцію навмисно — саме тут була помилка
 * попередньої версії: вона рахувала `cache.size > broken.size`, тому один
 * зламаний файл поруч з одним робочим давав «звуку немає», хоча звук був.
 * Плюс `cache` наповнювався в момент СТВОРЕННЯ Howl, до завантаження, тож
 * «є» могло означати «ще вантажиться» або «зараз впаде».
 */
export function aggregateStatus(states: Iterable<SoundLoad>): SoundStatus {
  let ready = false;
  let loading = false;
  let broken = false;

  for (const state of states) {
    if (state === 'ready') ready = true;
    else if (state === 'loading') loading = true;
    else broken = true;
  }

  // Один робочий файл важливіший за десять зламаних: звук у застосунку є.
  if (ready) return 'ready';
  // Поки щось вантажиться — вироку ще немає.
  if (loading) return 'unknown';
  return broken ? 'broken' : 'unknown';
}

class SoundManager {
  private cache = new Map<SoundKey, Howl>();
  private states = new Map<SoundKey, SoundLoad>();
  private listeners = new Set<() => void>();

  private get enabled(): boolean {
    return useUiStore.getState().soundEnabled;
  }

  private set(key: SoundKey, state: SoundLoad): void {
    if (this.states.get(key) === state) return;
    this.states.set(key, state);
    for (const listener of this.listeners) {
      try {
        listener();
      } catch {
        /* один поганий підписник не має ламати решту */
      }
    }
  }

  private resolve(key: SoundKey): Howl | null {
    if (this.states.get(key) === 'broken') return null;
    const cached = this.cache.get(key);
    if (cached) return cached;

    const spec = SOUNDS[key];
    try {
      const howl = new Howl({
        src: spec.src,
        volume: spec.volume,
        preload: true,
        html5: false,
      });
      this.set(key, 'loading');
      howl.on('load', () => this.set(key, 'ready'));
      howl.on('loaderror', () => {
        this.cache.delete(key);
        this.set(key, 'broken');
      });
      howl.on('playerror', () => this.set(key, 'broken'));
      this.cache.set(key, howl);
      return howl;
    } catch {
      this.set(key, 'broken');
      return null;
    }
  }

  play(key: SoundKey): void {
    if (!this.enabled) return;
    const howl = this.resolve(key);
    if (!howl) return;
    try {
      howl.play();
    } catch {
      this.set(key, 'broken');
    }
  }

  /** Прогрів після першого тапу — інакше iOS не дасть відтворити звук. */
  warmup(): void {
    if (!this.enabled) return;
    this.resolve('tap');
  }

  /**
   * Спробувати завантажити файл, нічого не граючи.
   * Потрібно кабінету батьків: там треба ЗНАТИ стан, не влаштовуючи концерт.
   */
  probe(): void {
    if (!this.enabled) return;
    this.resolve('tap');
  }

  status(): SoundStatus {
    return aggregateStatus(this.states.values());
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export const sound = new SoundManager();

export function playSound(key: SoundKey): void {
  sound.play(key);
}

export function probeSound(): void {
  sound.probe();
}

/** Для useSyncExternalStore — див. useSoundStatus.ts. */
export function subscribeSoundStatus(listener: () => void): () => void {
  return sound.subscribe(listener);
}

export function getSoundStatus(): SoundStatus {
  return sound.status();
}
